// src/pages/QRTool/QRToolPage.tsx
// Clean, Google-inspired QR Code Generator with SEO optimization

import React, { useState, useCallback } from 'react';

import { saveAs } from 'file-saver';
import SEO from '../../../components/SEO/SEO';
import { ToolSchema, BreadcrumbSchema, HowToSchema } from '../../../components/Schema';
import { seoConfig } from '../../../config/seo';
import {
    CheckCircle, Shield, Zap, QrCode as QrIcon,
    Palette, Download, Copy, Settings,
    FileText, Scan, Link as LinkIcon, RefreshCw
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage';
import apiService from '../../../services/apiService';
import {
    ToolPageLayout,
    ToolInfoStrip,
    ToolSection,
    ToolStepCard,
    ToolFeatureCard,
    ToolFaqCta,
    ToolRelatedCard,
} from '../components';
import SecurityBadge from '../../../components/Shared/SecurityBadge/SecurityBadge';
import { toast } from 'react-toastify';
import tokenManager from '../../../utils/tokenManager';

type BorderStyle = 'no-border' | 'square';

const QRToolPage: React.FC = () => {
    // QR State
    const [qrData, setQrData] = useState<string>('');
    const [generatedQrId, setGeneratedQrId] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [dataError, setDataError] = useState<string | null>(null);

    // Appearance
    const [fillColor, setFillColor] = useState<string>('#000000');
    const [backgroundColor, setBackgroundColor] = useState<string>('#FFFFFF');
    const [borderStyle, setBorderStyle] = useState<BorderStyle>('no-border');

    // Preview
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    // Update preview - MUST be defined before handleGenerateQR
    const updatePreview = useCallback((qrId: string) => {
        if (!qrId) return;

        const border = borderStyle === 'no-border' ? 'none' : 'square';
        const url = `${apiService.baseURL}/api/tools/qrcode/download/${qrId}?fill_color=${encodeURIComponent(fillColor)}&bg_color=${encodeURIComponent(backgroundColor)}&border_style=${encodeURIComponent(border)}&nocache=${Date.now()}`;
        setPreviewUrl(url);
    }, [fillColor, backgroundColor, borderStyle]);

    // Generate QR Code
    const handleGenerateQR = useCallback(async () => {
        if (!qrData.trim()) {
            toast.error('Please enter data to encode');
            return;
        }

        if (qrData.length > 2000) {
            toast.error('Data too large (max 2000 characters)');
            return;
        }

        setIsGenerating(true);
        setError(null);

        try {
            // Backend expects FormData, not JSON
            const formData = new FormData();
            formData.append('data', qrData);
            formData.append('error_correction', 'M');
            formData.append('box_size', '10');
            formData.append('border', '4');
            formData.append('fill_color', fillColor);
            formData.append('back_color', backgroundColor);
            formData.append('text_config', '{}');

            const securityHeaders = await tokenManager.getSecurityHeader() || {};
            const response = await fetch(`${apiService.baseURL}/api/tools/qrcode/generate`, {
                method: 'POST',
                headers: securityHeaders as HeadersInit,
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to generate QR code');
            }

            const data = await response.json();

            if (data && data.file_id) {
                setGeneratedQrId(data.file_id);
                updatePreview(data.file_id);
                toast.success('QR Code generated successfully!');
            } else {
                toast.error('Failed to generate QR code');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to generate QR code');
            toast.error('Generation failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    }, [qrData, fillColor, backgroundColor, updatePreview]);

    // Download QR
    const handleDownload = useCallback(() => {
        if (!generatedQrId) return;

        const border = borderStyle === 'no-border' ? 'none' : 'square';
        const downloadUrl = `${apiService.baseURL}/api/tools/qrcode/download/${generatedQrId}?fill_color=${encodeURIComponent(fillColor)}&bg_color=${encodeURIComponent(backgroundColor)}&border_style=${encodeURIComponent(border)}&format=png`;

        saveAs(downloadUrl, 'qrcode.png');
        toast.success('Download started');
    }, [generatedQrId, fillColor, backgroundColor, borderStyle]);

    // Copy data
    const handleCopyData = () => {
        navigator.clipboard.writeText(qrData);
        toast.success('Data copied to clipboard!');
    };

    const handleReset = () => {
        setQrData('');
        setGeneratedQrId(null);
        setPreviewUrl(null);
        setError(null);
        setDataError(null);
        setFillColor('#000000');
        setBackgroundColor('#FFFFFF');
        setBorderStyle('no-border');
        toast.info('Reset complete');
    };

    return (
        <>
            <SEO {...seoConfig.qrcode} />
            <ToolSchema
                name="QR Code Generator"
                description="Free online QR code generator with custom colors, logos, and sizes. Create QR codes for URLs, text, WiFi, contacts, and more."
                url="/tools/qr-code-generator"
                applicationCategory="UtilitiesApplication"
                featureList={[
                    "Generate QR codes instantly",
                    "Custom colors and styles",
                    "Add logos to QR codes",
                    "Multiple data types supported",
                    "Download in PNG format",
                    "High-resolution output"
                ]}
            />
            <BreadcrumbSchema
                breadcrumbs={[
                    { name: 'Home', url: 'https://www.filefusion.app/' },
                    { name: 'Tools', url: 'https://www.filefusion.app/tools' },
                    { name: 'QR Code Generator', url: 'https://www.filefusion.app/tools/qr-code-generator' }
                ]}
            />
            <HowToSchema
                name="How to Create a QR Code Online"
                description="Generate custom QR codes for any purpose in just a few clicks."
                totalTime="PT1M"
                steps={[
                    { name: "Enter Your Data", text: "Type or paste the URL, text, or information you want to encode." },
                    { name: "Customize Appearance", text: "Choose colors, add borders, or upload a logo for branding." },
                    { name: "Generate QR Code", text: "Click generate to create your QR code preview." },
                    { name: "Download", text: "Download your QR code in PNG format for use anywhere." }
                ]}
                tool={["Web Browser"]}
            />

            <ToolPageLayout
                icon={<QrIcon size={32} strokeWidth={1.5} />}
                title="Free QR Code Generator"
                subtitle="Create high-quality, customizable QR codes for any purpose. Perfect for marketing, business cards, and digital sharing."
                tags={(
                    <>
                        <SecurityBadge type="secure" />
                        <SecurityBadge type="deleted" />
                        <SecurityBadge type="private" />
                    </>
                )}
            >

                <div className="flex-1 min-w-0">
                    {error && (
                        <ErrorMessage
                            type="unknown"
                            message={error}
                            onClose={() => setError(null)}
                        />
                    )}

                    {dataError && (
                        <div className="px-[16px] py-[12px] bg-[#fef7e0] border-[1px] border-[#f9d71c] rounded-[12px] text-[#b45309] text-[0.875rem] mb-[20px] text-center">
                            {dataError}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-[1fr_400px] gap-[32px] bg-white border-[1px] border-[#dadce0] rounded-[12px] p-[24px] md:p-[32px] shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)]">
                        <div className="flex flex-col gap-[32px]">
                            <div className="flex flex-col gap-[8px]">
                                <label htmlFor="qr-data" className="flex items-center gap-[8px] text-[0.9375rem] font-medium text-[#202124]">
                                    <LinkIcon size={18} />
                                    Enter Data to Encode
                                </label>
                                <textarea
                                    id="qr-data"
                                    className="w-full p-[12px] border-[1px] border-[#dadce0] rounded-[12px] text-[0.9375rem] font-inherit text-[#202124] resize-y transition-colors duration-200 outline-none focus:border-[#1a73e8] focus:shadow-[0_0_0_3px_#e8f0fe]"
                                    placeholder="Enter URL, text, WiFi credentials, or any data you want to encode..."
                                    value={qrData}
                                    onChange={(e) => setQrData(e.target.value)}
                                    rows={6}
                                    maxLength={2000}
                                />
                                <div className="text-[0.8125rem] text-[#80868b] text-right">
                                    {qrData.length} / 2000 characters
                                </div>
                            </div>

                            <div className="p-[20px] bg-[#f8f9fa] border-[1px] border-[#dadce0] rounded-[12px]">
                                <h3 className="flex items-center gap-[8px] text-[1rem] font-medium text-[#202124] m-0 mb-[16px]">
                                    <Palette size={18} />
                                    Customize Appearance
                                </h3>

                                <div className="flex flex-col gap-[16px] mb-[20px]">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-[12px]">
                                        <label htmlFor="fill-color-picker" className="text-[0.875rem] text-[#5f6368] min-w-[120px]">QR Code Color</label>
                                        <input
                                            id="fill-color-picker"
                                            type="color"
                                            value={fillColor}
                                            onChange={(e) => setFillColor(e.target.value)}
                                            className="w-[48px] h-[40px] border-[1px] border-[#dadce0] rounded-[4px] cursor-pointer"
                                        />
                                        <span className="text-[0.8125rem] text-[#80868b] font-mono">{fillColor}</span>
                                    </div>

                                    <div className="flex flex-col sm:flex-row sm:items-center gap-[12px]">
                                        <label htmlFor="bg-color-picker" className="text-[0.875rem] text-[#5f6368] min-w-[120px]">Background Color</label>
                                        <input
                                            id="bg-color-picker"
                                            type="color"
                                            value={backgroundColor}
                                            onChange={(e) => setBackgroundColor(e.target.value)}
                                            className="w-[48px] h-[40px] border-[1px] border-[#dadce0] rounded-[4px] cursor-pointer"
                                        />
                                        <span className="text-[0.8125rem] text-[#80868b] font-mono">{backgroundColor}</span>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-[12px]">
                                    <label className="text-[0.875rem] text-[#5f6368]">Border Style</label>
                                    <div className="flex gap-[8px]">
                                        <button
                                            className={`flex-1 px-[16px] py-[8px] rounded-[4px] text-[0.875rem] font-medium transition-all duration-200 border-[1px] ${borderStyle === 'no-border' ? 'bg-[#1a73e8] text-white border-[#1a73e8]' : 'bg-white text-[#5f6368] border-[#dadce0] hover:bg-[#f8f9fa]'}`}
                                            onClick={() => setBorderStyle('no-border')}
                                        >
                                            No Border
                                        </button>
                                        <button
                                            className={`flex-1 px-[16px] py-[8px] rounded-[4px] text-[0.875rem] font-medium transition-all duration-200 border-[1px] ${borderStyle === 'square' ? 'bg-[#1a73e8] text-white border-[#1a73e8]' : 'bg-white text-[#5f6368] border-[#dadce0] hover:bg-[#f8f9fa]'}`}
                                            onClick={() => setBorderStyle('square')}
                                        >
                                            Square Border
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-[16px]">
                                <button
                                    className="flex items-center justify-center gap-[8px] px-[28px] py-[14px] bg-[#1a73e8] text-white border-none rounded-[4px] text-[1rem] font-medium cursor-pointer transition-all duration-200 hover:bg-[#1557b0] hover:shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)] disabled:bg-[#dadce0] disabled:text-[#80868b] disabled:cursor-not-allowed disabled:shadow-none"
                                    onClick={handleGenerateQR}
                                    disabled={isGenerating || !qrData.trim() || !!dataError}
                                >
                                    {isGenerating ? (
                                        <>
                                            <LoadingSpinner size="small" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <QrIcon size={20} />
                                            Generate QR Code
                                        </>
                                    )}
                                </button>
                                <button className="flex items-center justify-center w-[48px] h-[48px] rounded-[8px] bg-white border-[1px] border-[#dadce0] text-[#5f6368] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] hover:text-[#202124] hover:border-[#5f6368]" onClick={handleReset} title="Reset Form">
                                    <RefreshCw size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col items-center order-[-1] md:order-none">
                            <h3 className="text-[1rem] font-medium text-[#202124] m-0 mb-[20px] self-start">Preview Result</h3>
                            {previewUrl ? (
                                <div className="flex flex-col items-center gap-[20px] w-full">
                                    <img
                                        src={previewUrl}
                                        alt="Generated QR Code"
                                        className="w-full max-w-[300px] h-auto border-[1px] border-[#dadce0] rounded-[12px] shadow-[0_1px_2px_rgba(60,64,67,0.3)]"
                                    />
                                    <div className="flex flex-col gap-[10px] w-full">
                                        <button
                                            className="w-full flex items-center justify-center gap-[8px] px-[20px] py-[10px] text-[0.9375rem] font-medium rounded-[4px] cursor-pointer transition-all duration-200 bg-[#1a73e8] text-white border-none hover:bg-[#1557b0]"
                                            onClick={handleDownload}
                                        >
                                            <Download size={18} />
                                            Download PNG
                                        </button>
                                        <button
                                            className="w-full flex items-center justify-center gap-[8px] px-[20px] py-[10px] text-[0.9375rem] font-medium rounded-[4px] cursor-pointer transition-all duration-200 bg-white text-[#5f6368] border-[1px] border-[#dadce0] hover:bg-[#f8f9fa]"
                                            onClick={handleCopyData}
                                        >
                                            <Copy size={18} />
                                            Copy Data
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-[60px] px-[20px] bg-[#f8f9fa] border-[2px] border-dashed border-[#dadce0] rounded-[12px] text-center w-full">
                                    <QrIcon size={64} className="text-[#80868b] mb-[16px]" />
                                    <p className="text-[1rem] text-[#202124] m-0 mb-[8px]">Your QR code will appear here</p>
                                    <span className="text-[0.875rem] text-[#80868b]">Enter data and click Generate</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </ToolPageLayout>

            <ToolInfoStrip>
                <ToolInfoStrip.Section variant="privacy">
                    <h3><Shield size={20} />Privacy Shield</h3>
                    <p>Your data is processed securely via encrypted transit. We never store your information or the generated QR codes on our servers.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><Zap size={20} />Pro Tips</h3>
                    <p>Use high contrast colors for the best readability. Dark QR codes on light backgrounds are the most reliable for scanners.</p>
                </ToolInfoStrip.Section>
                <ToolInfoStrip.Section>
                    <h3><CheckCircle size={20} />Supported Data</h3>
                    <ul>
                        <li>URLs &amp; Links</li>
                        <li>WiFi Credentials</li>
                        <li>Contact Info (vCard)</li>
                    </ul>
                </ToolInfoStrip.Section>
            </ToolInfoStrip>


            <ToolSection variant="light" title="How to Create a QR Code in 3 Steps" grid="steps">
                <ToolStepCard number={1} title="Enter Your Data" description="Type or paste the information you want to encode. This can be a website URL, plain text, contact details, WiFi password, or any other data up to 2000 characters." />
                <ToolStepCard number={2} title="Customize Appearance" description="Choose custom colors for your QR code and background. Select border style and adjust other visual settings to match your branding or preferences." />
                <ToolStepCard number={3} title="Generate & Download" description="Click generate to create your QR code. Preview it instantly, then download in high-resolution PNG format ready for printing or digital use." />
            </ToolSection>


            <ToolSection variant="muted" title="Powerful QR Code Features" description="Our QR code generator offers professional features for creating custom QR codes for any purpose, from business cards to marketing materials." grid="features">
                <ToolFeatureCard><QrIcon size={28} /><h3>Instant Generation</h3><p>Create QR codes in seconds. Simply enter your data, customize colors, and generate. Your QR code appears instantly with preview before download.</p></ToolFeatureCard>
                <ToolFeatureCard><Palette size={28} /><h3>Custom Colors</h3><p>Personalize QR codes with custom foreground and background colors. Match your brand colors or create eye-catching designs for marketing materials.</p></ToolFeatureCard>
                <ToolFeatureCard><Settings size={28} /><h3>Multiple Data Types</h3><p>Encode URLs, plain text, contact information, WiFi credentials, email addresses, phone numbers, and more. Versatile enough for any use case.</p></ToolFeatureCard>
                <ToolFeatureCard><Download size={28} /><h3>High-Resolution Output</h3><p>Download QR codes in high-resolution PNG format perfect for printing on business cards, posters, product packaging, or digital use.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="muted" title="Popular QR Code Use Cases" grid="features">
                <ToolFeatureCard><h3>Business Cards</h3><p>Add QR codes to business cards linking to your website, LinkedIn profile, or contact information. Recipients can instantly save your details or visit your site.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Marketing Materials</h3><p>Include QR codes on flyers, posters, and advertisements to drive traffic to landing pages, promotions, or product information. Track campaign effectiveness.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>WiFi Sharing</h3><p>Create QR codes for WiFi networks so guests can connect instantly without typing passwords. Perfect for cafes, offices, or home networks.</p></ToolFeatureCard>
                <ToolFeatureCard><h3>Product Packaging</h3><p>Add QR codes to product packaging linking to user manuals, assembly instructions, warranty registration, or customer support resources.</p></ToolFeatureCard>
            </ToolSection>

            <ToolSection variant="light" title="Frequently Asked Questions" grid="none">
                <div className="flex flex-col gap-[20px] mb-[32px]">
                    <ToolFeatureCard variant="faq"><h3>What data can I encode in a QR code?</h3><p>You can encode up to 2000 characters including URLs, plain text, email addresses, phone numbers, SMS messages, WiFi credentials (SSID and password), contact information (vCard), and geographic coordinates.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Can I customize QR code colors?</h3><p>Yes! Customize both the QR code color (foreground) and background color to match your branding. Ensure sufficient contrast between colors for reliable scanning.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>What format are QR codes downloaded in?</h3><p>QR codes are downloaded as high-resolution PNG images suitable for both digital and print use. The images are optimized for scanning at various sizes.</p></ToolFeatureCard>
                    <ToolFeatureCard variant="faq"><h3>Will my QR code work on all devices?</h3><p>Yes. QR codes generated by our tool follow international standards and work with all modern smartphones, tablets, and QR code scanner apps on iOS and Android devices.</p></ToolFeatureCard>
                </div>
                <ToolFaqCta prompt="More questions?" to="/faq" label="Visit our FAQ page" />
            </ToolSection>

            <ToolSection variant="muted" title="Related QR &amp; Scanning Tools" grid="related">
                <ToolRelatedCard to="/tools/barcode-scanner" icon={<Scan size={24} />} title="Barcode Scanner" description="Scan and decode QR codes and barcodes using your camera" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/compare-text" icon={<FileText size={24} />} title="Text Comparison" description="Compare two text files or QR code data side-by-side" linkLabel="Try Tool" />
                <ToolRelatedCard to="/tools/compress-pdf-images" icon={<Download size={24} />} title="File Size Reducer" description="Compress images and PDFs to reduce file sizes" linkLabel="Try Tool" />
            </ToolSection>
        </>
    );
};

export default QRToolPage;
